import type { IdentifiedOrDeletedAuthor, ThreadAuthor } from "@compass/shared/schema/author";
import {
    type DescribeAuthorOptions,
    describeAuthor,
} from "@compass/shared/schema/authorPresentation";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import anonymousAvatarSvg from "assets/avatars/anonymous.svg";
import compassTeamAvatarSvg from "assets/avatars/compass-team.svg";
import deletedAvatarSvg from "assets/avatars/deleted.svg";
import { Avatar, Tooltip } from "components/elements";
import { cn } from "lib/cn";
import { ProfileTooltip } from "screens/profile/ProfileTooltip";

type AnyAuthor = ThreadAuthor | IdentifiedOrDeletedAuthor;

/** Which profile route this surface links to. Both render the same profile. */
export type ProfileRoute = "/profile/$id" | "/social/profile/$id";

interface LinkOptions extends DescribeAuthorOptions {
    /** Set false to render without a profile link even for an identified author. */
    link?: boolean;
    profileRoute?: ProfileRoute;
    /** `_blank` for surfaces that open profiles in a new tab. */
    target?: string;
}

const AVATAR_SVG = {
    anonymous: anonymousAvatarSvg,
    "compass-team": compassTeamAvatarSvg,
    deleted: deletedAvatarSvg,
} as const;

function ProfileLink({
    id,
    route,
    target,
    className,
    children,
}: {
    id: number;
    route: ProfileRoute;
    target?: string;
    className?: string;
    children: ReactNode;
}) {
    return (
        <Link
            to={route}
            params={{ id: String(id) }}
            target={target}
            className={cn("no-underline hover:no-underline", className)}
        >
            {children}
        </Link>
    );
}

/**
 * The avatar for any author chip (PRTL-1385). Photo or seeded initials for a
 * real user, the anonymous / Compass Team / deleted marks otherwise, and a
 * profile link only when `describeAuthor` says there is a profile.
 */
export const AuthorAvatar = ({
    author,
    className,
    seed,
    tooltip = false,
    link = true,
    profileRoute = "/profile/$id",
    target,
    surface,
}: LinkOptions & {
    author: AnyAuthor;
    className?: string;
    /** Colour seed for the initials fallback; use the content id so an
     *  anonymous mark cannot be correlated to a user. */
    seed?: string | number;
    /** Show the hover profile card (identified authors only). */
    tooltip?: boolean;
}) => {
    const p = describeAuthor(author, { surface });
    const avatar = (
        <Avatar
            className={className}
            photoUrl={p.avatar.kind === "user" ? p.avatar.url : AVATAR_SVG[p.avatar.kind]}
            initials={p.avatar.kind === "user" ? p.avatar.initials : undefined}
            name={p.name}
            seed={seed}
        />
    );
    if (p.profileId === null || !link) return avatar;
    const linked = (
        <ProfileLink id={p.profileId} route={profileRoute} target={target} className="shrink-0">
            {avatar}
        </ProfileLink>
    );
    return tooltip ? (
        <Tooltip content={<ProfileTooltip authorId={String(p.profileId)} />}>{linked}</Tooltip>
    ) : (
        linked
    );
};

/**
 * The name for any author chip: linked for an identified author, italic when
 * their social access is off, and — for a superuser looking at anonymous
 * content — the real name on hover under a dashed underline.
 */
export const AuthorName = ({
    author,
    className,
    link = true,
    profileRoute = "/profile/$id",
    target,
    surface,
    children,
}: LinkOptions & {
    author: AnyAuthor;
    className?: string;
    /** Extra inline content after the name (badges, suffixes). */
    children?: ReactNode;
}) => {
    const p = describeAuthor(author, { surface });
    const label = p.realName ? (
        <Tooltip content={p.realName}>
            <span className="cursor-help underline decoration-gray-400 decoration-dashed underline-offset-2">
                {p.name}
            </span>
        </Tooltip>
    ) : (
        p.name
    );
    const text = (
        <span className={cn(className, { italic: p.isInactive })}>
            {label}
            {children}
        </span>
    );
    if (p.profileId === null || !link) return text;
    return (
        <ProfileLink id={p.profileId} route={profileRoute} target={target}>
            {text}
        </ProfileLink>
    );
};
